import { ActiveTab } from 'common/types/interface'

/**
 * How many interfaces stay in the Redux cache. Returning to a cached interface
 * reuses its layout and its component results instead of asking the engine to
 * re-render everything.
 */
export const MAX_CACHED_INTERFACE_TABS = 10

/**
 * Upper bound for the results kept for interfaces the user is not looking at,
 * measured in UTF-16 characters of the raw websocket payloads that produced
 * them - not bytes, and not heap. Parsed into objects the same payload takes
 * several times more memory, so the real ceiling this implies is a multiple of
 * the number below, and the interfaces excluded from the count (the one in use,
 * the default, and the one being opened) sit on top of it.
 *
 * The interface in use is excluded because nothing can be done about its size:
 * counting it would make a single huge dashboard evict the whole cache on
 * every result it receives.
 *
 * This number is deliberately conservative and NOT calibrated against real
 * memory. To calibrate it, read `performance.memory.usedJSHeapSize` before and
 * after filling the cache with a heavy customer model, and raise it only as far
 * as the measurement supports.
 */
export const MAX_CACHED_INTERFACE_PAYLOAD_CHARS = 20 * 1024 * 1024

/** Payload characters received per component, grouped by interface. */
export type InterfaceComponentSizes = { [interfaceId: string]: { [componentId: string]: number } }

export const getInterfaceSize = (
  componentSizes: InterfaceComponentSizes | undefined,
  interfaceId: string
): number => {
  const sizes = componentSizes?.[interfaceId]
  if (!sizes) return 0
  let total = 0
  for (const componentId in sizes) {
    total += sizes[componentId]
  }
  return total
}

/**
 * Only what eviction could actually free counts against the budget: sizes
 * recorded for an interface outside `activeTabs`, or for a protected one, are
 * not reclaimable, and counting them would keep the cache permanently over the
 * limit - dropping every other interface on each result that arrives.
 */
const getEvictableSize = (
  componentSizes: InterfaceComponentSizes | undefined,
  activeTabs: ActiveTab[],
  protectedIds: Set<string>
): number => {
  if (!componentSizes) return 0
  let total = 0
  activeTabs.forEach((tab) => {
    if (protectedIds.has(tab.activeTabId)) return
    total += getInterfaceSize(componentSizes, tab.activeTabId)
  })
  return total
}

export interface CacheEvictionResult {
  activeTabs: ActiveTab[]
  evictedIds: string[]
}

/**
 * Drops cached interfaces until the cache fits both limits, least recently used
 * first. `protectedIds` (the interface being opened and the default one) are
 * never dropped, so the cache can legitimately end up over the limits when
 * everything left in it is protected.
 *
 * Callers must also remove `evictedIds` from every other per-interface slice
 * (results, sizes, view state) - that is where the memory actually is.
 */
export const evictCachedInterfaces = (
  activeTabs: ActiveTab[],
  componentSizes: InterfaceComponentSizes | undefined,
  protectedIds: (string | undefined)[]
): CacheEvictionResult => {
  const protectedSet = new Set(protectedIds.filter(Boolean) as string[])
  const evictedIds: string[] = []
  const tabs = [...activeTabs]
  let evictableChars = getEvictableSize(componentSizes, tabs, protectedSet)

  const isOverLimit = () =>
    tabs.length > MAX_CACHED_INTERFACE_TABS || evictableChars > MAX_CACHED_INTERFACE_PAYLOAD_CHARS

  while (isOverLimit()) {
    let lruIndex = -1
    let lruTime = Number.POSITIVE_INFINITY
    tabs.forEach((tab, index) => {
      if (protectedSet.has(tab.activeTabId)) return
      const lastAccessedAt = tab.lastAccessedAt ?? 0
      if (lastAccessedAt < lruTime) {
        lruTime = lastAccessedAt
        lruIndex = index
      }
    })
    if (lruIndex === -1) break

    const [evicted] = tabs.splice(lruIndex, 1)
    evictedIds.push(evicted.activeTabId)
    evictableChars -= getInterfaceSize(componentSizes, evicted.activeTabId)
  }

  return { activeTabs: tabs, evictedIds }
}

/** Removes the given interfaces from a per-interface map, keeping it untouched when nothing matches. */
export const omitInterfaces = <T extends { [interfaceId: string]: unknown }>(
  map: T | undefined,
  interfaceIds: string[]
): T | undefined => {
  if (!map || interfaceIds.length === 0) return map
  const idsToRemove = interfaceIds.filter((id) => id in map)
  if (idsToRemove.length === 0) return map
  const next = { ...map } as T
  idsToRemove.forEach((id) => delete next[id])
  return next
}
